import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import * as i2 from 'se-fe-api';
import { SeFeApiModule } from 'se-fe-api';

const resizableMimeMap = {
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
};
const imgMimeMap = {
    gif: 'image/gif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
};
const textMimeMap = {
    csv: 'text/csv',
    html: 'text/html',
    txt: 'text/plain',
};
const docsMimeMap = {
    xls: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    json: 'application/json',
    odt: 'application/vnd.oasis.opendocument.text',
    pdf: 'application/pdf',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};
const audioMimeMap = {
    aac: 'audio/aac',
    ac3: 'audio/ac3',
    aiff: 'audio/x-aiff',
    amr: 'audio/amr',
    flac: 'audio/flac',
    m4a: 'audio/x-m4a',
    mp3: 'audio/mpeg',
    ogg: 'audio/ogg',
    snd: 'audio/basic',
    wav: 'audio/wav',
    weba: 'audio/webm',
};
const videoMimeMap = {
    '3gpp': 'video/3gpp',
    avi: 'video/x-msvideo',
    m4v: 'video/x-m4v',
    m3u8: 'application/x-mpegURL',
    mkv: 'video/x-matroska',
    mov: 'video/quicktime',
    mp4: 'video/mp4',
    mpeg: 'video/mpeg',
    ogv: 'video/ogg',
    ts: 'video/MP2T',
    webm: 'video/webm',
    wmv: 'video/x-ms-wmv',
};
const attachmentMimeMap = {
    ...resizableMimeMap,
    ...imgMimeMap,
    ...textMimeMap,
    ...docsMimeMap,
    // Asset service does not currently support opening audio and video files
    // ...audioMimeMap,
    // ...videoMimeMap,
};
const mimeCategories = Object
    .keys(attachmentMimeMap)
    .sort()
    .reduce((acc, extension) => {
    const category = attachmentMimeMap[extension].split('/')[0];
    acc[category] = acc[category] || [];
    acc[category].push(extension);
    return acc;
}, {});

function sanitizeFileName(fileName) {
    const newFileName = fileName.replace(/[^a-zA-Z0-9_() .-]+/g, '');
    const fileExtension = `.${newFileName.split('.')[1]}`;
    return newFileName === fileExtension ? `file${fileExtension}` : newFileName;
}
class SeFeAttachment {
    constructor(file) {
        this.guid = '';
        this.error = '';
        if (file instanceof File) {
            this.guid = '';
            this.file = file;
            this.filename = sanitizeFileName(file.name);
            this.size = file.size;
            this.content_type = file.type; // eslint-disable-line @typescript-eslint/camelcase
            this.status = 'uploading';
        }
        else {
            this.status = 'generic_error';
        }
    }
}

/* eslint-disable @typescript-eslint/camelcase */
class SeFeAttachmentError extends Error {
}
class SeFeAttachmentFileMissingError extends SeFeAttachmentError {
}
class SeFeAttachmentFileTypeInvalidError extends SeFeAttachmentError {
}
class SeFeAttachmentFileTooLargeError extends SeFeAttachmentError {
}
class SeFeAttachmentService {
    constructor(http, apiService, env) {
        this.http = http;
        this.apiService = apiService;
        this.env = env;
    }
    async upload(file, assetType = SeFeAttachmentService.ATTACHMENT) {
        const attachment = this.validAttachment(file);
        const presignResult = await this.presignFile(attachment, assetType);
        const guid = (presignResult?.result?.fields?.key || '').split('/')?.[1] || '';
        const url = this.getUrl(this.env.assetService, this.getObjectUrlPath(guid, attachment.filename));
        await this.uploadFile(attachment, presignResult?.result);
        Object.assign(attachment, { guid, url, status: 'uploaded' });
        return attachment;
    }
    validAttachment(attachment) {
        if (attachment instanceof File)
            attachment = new SeFeAttachment(attachment);
        if (!attachment?.file)
            this.handleUploadErrors(null, attachment);
        return attachment;
    }
    async presignFile(attachment, assetType) {
        const { name, size } = attachment.file;
        const opts = {
            withCredentials: true,
            headers: { Accept: 'application/json' },
        };
        const data = {
            file_name: name,
            file_size: size,
            asset_type: assetType,
        };
        try {
            return await this.apiService.post('v3/assets/presign-post', data, opts).toPromise();
        }
        catch (err) {
            this.handleUploadErrors('presign_failed', attachment);
        }
    }
    async uploadFile(attachment, { url, fields: data }) {
        let formData = new FormData();
        Object.entries(data).forEach(([key, val]) => formData.append(key, val));
        formData.append('file', attachment.file);
        let options = {
            observe: 'response',
            // TODO: Content-Type for uploaded file is returned as binary/octet-stream from S3
            // headers: { ContentType: undefined /* data.ContentType */ }, // let the browser set this, it adds boundary
        };
        try {
            await this.http.post(url, formData, options)?.toPromise();
        }
        catch (err) {
            this.handleUploadErrors(err, attachment);
        }
    }
    // Utility functions
    getObjectUrlPath(guid, fileName) {
        return `attachment/${guid}/${encodeURIComponent(fileName).replace(/%20/g, '+')}`;
    }
    getUrl(base, path) {
        return String(Object.assign(new URL(path, base), { protocol: 'https' }));
    }
    handleUploadErrors(err, attachment) {
        attachment.status = 'generic_error';
        attachment.error = this.errorMessage(err, attachment);
        throw new SeFeAttachmentError(); // Unexpected error type, throw generic
    }
    errorMessage(err, attachment) {
        if (!attachment?.file)
            return 'file_missing';
        if (err?.data?.result === 'Invalid File')
            return 'file_type_invalid';
        if (this.findXmlErrorCode(err?.data) === 'EntityTooLarge')
            return 'file_too_large';
        return err;
    }
    findXmlErrorCode(xml) {
        const xmlDoc = new DOMParser().parseFromString(xml, 'text/xml');
        return xmlDoc.evaluate('//Error/Code', xmlDoc, null, XPathResult.STRING_TYPE)?.stringValue || '';
    }
}
SeFeAttachmentService.ATTACHMENT = 'attachment';
SeFeAttachmentService.MIME_CATEGORIES = mimeCategories;
SeFeAttachmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentService, deps: [{ token: i1.HttpClient }, { token: i2.SeFeApiService }, { token: 'env' }], target: i0.ɵɵFactoryTarget.Injectable });
SeFeAttachmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.SeFeApiService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }]; } });

class SeFeAttachmentServiceModule {
    static forRoot(environment) {
        return {
            ngModule: SeFeAttachmentServiceModule,
            providers: [
                { provide: 'env', useValue: environment }
            ]
        };
    }
}
SeFeAttachmentServiceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentServiceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeAttachmentServiceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentServiceModule, imports: [SeFeApiModule] });
SeFeAttachmentServiceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentServiceModule, imports: [SeFeApiModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SeFeAttachmentServiceModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SeFeApiModule]
                }]
        }] });

/*
 * Public API Surface of se-fe-attachment-service
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeAttachment, SeFeAttachmentError, SeFeAttachmentFileMissingError, SeFeAttachmentFileTooLargeError, SeFeAttachmentFileTypeInvalidError, SeFeAttachmentService, SeFeAttachmentServiceModule, attachmentMimeMap, audioMimeMap, docsMimeMap, imgMimeMap, mimeCategories, resizableMimeMap, textMimeMap, videoMimeMap };
